import React from 'react';
import { Button } from '@/components/ui/button';
import { formatOrdinalDate } from '@/utils/formatOrdinalDate';

interface ReservePanelProps {
  id: string;
  title: string;
  prize: string;
  /** ISO timestamp for when questions will be posted, or null if not yet set. */
  questionsPostedAt: string | null;
  /** Whether the current user already has a reservation for this pickem. */
  hasReservation?: boolean;
  isSubmitting?: boolean;
  onEnter?: () => void;
  className?: string;
}

/**
 * "Reserve panel" — DKPLAT-528. Shown on the Reserve Entry screen for pickems
 * that don't have questions posted yet: contest name, prize pool, a yellow
 * "Questions will be posted <date>" line, and the "Enter | Free" CTA that
 * opens the Confirm Entry modal. Once the user has a reservation, the CTA is
 * replaced with a disabled confirmation state (there is no un-reserve flow).
 */
const ReservePanel: React.FC<ReservePanelProps> = ({
  id,
  title,
  prize,
  questionsPostedAt,
  hasReservation = false,
  isSubmitting = false,
  onEnter,
  className = '',
}) => {
  const postedDateLabel = formatOrdinalDate(questionsPostedAt);

  return (
    <div
      data-test-id={`reserve-panel-${id}`}
      className={`rounded-xl p-6 bg-[#242424] border border-[#373737] text-center space-y-4 ${className}`}
    >
      <h2
        data-test-id={`reserve-panel-title-${id}`}
        className="font-gilroy font-bold text-lg text-text-primary tracking-wide"
      >
        {title}
      </h2>

      <p className="font-gilroy text-sm text-text-secondary">
        Prize Pool:{' '}
        <span data-test-id={`reserve-panel-prize-${id}`} className="font-bold text-pickem-primary">
          {prize}
        </span>
      </p>

      {postedDateLabel && (
        <p
          data-test-id={`reserve-panel-questions-posted-${id}`}
          className="font-gilroy text-sm font-semibold text-yellow-500"
        >
          Questions will be posted {postedDateLabel}
        </p>
      )}

      {hasReservation ? (
        <div
          data-test-id={`reserve-panel-reserved-${id}`}
          className="w-full rounded-full py-3 font-gilroy font-bold text-pickem-primary border-2 border-pickem-primary"
        >
          You're In
        </div>
      ) : (
        <Button
          data-test-id={`reserve-panel-enter-${id}`}
          variant="pickem"
          className="w-full rounded-full"
          disabled={isSubmitting}
          onClick={onEnter}
        >
          Enter | Free
        </Button>
      )}
    </div>
  );
};

export default ReservePanel;
